import React, { ReactElement, useState, useRef, useMemo } from "react";
import classes from "./style.module.scss";
import cn from "classnames";
import DefaultFavoriteIcon from "../../SVGcomponents/default-icon-favorite.svg";
import ActiveFavoriteIcon from "../../SVGcomponents/active-favorite-icon.svg";
import DefaultChartIcon from "../../SVGcomponents/default-icon-chart.svg";
import ActiveChartIcon from "../../SVGcomponents/active-chart-icon.svg";
import DisabledTrashIcon from "../../SVGcomponents/diseible-trash.svg";
import DisabledCopyIcon from "../../SVGcomponents/disaible-copy.svg";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "remove"
  | "removeIcon"
  | "iconOutline"
  | "circleIcon"
  | "third"
  | "favorite"
  | "chart";
export type ButtonSize = "small" | "medium" | "large";

export const RESPONSIVE_BREAKPOINTS = {
  MOBILE: "(max-width: 767px)",
  TABLET: "(min-width: 768px) and (max-width: 1023px)",
  DESKTOP: "(min-width: 1024px)",
  MAC_13: "(max-width: 1440px)",
} as const;

export type CustomSize = {
  width?: string | number;
  height?: string | number;
  padding?: string;
  borderRadius?: string;
  fontSize?: string;
  gap?: string;
  lineHeight?: string;
  letterSpacing?: string;
};

export type ResponsiveBreakpointConfig = {
  mediaQuery: string;
  size?: ButtonSize;
  customSize?: CustomSize;
  hidden?: boolean;
};

export type ResponsiveBreakpoints = {
  [breakpointName: string]: ResponsiveBreakpointConfig;
};

export type ButtonProps = {
  sx?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  title?: string;
  type?: "button" | "submit" | "reset";
  inputAdornment?: null | string;
  disabled?: boolean;
  cls?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  responsiveBreakpoints?: ResponsiveBreakpoints;
  width?: string | number;
  icon?: string | React.ReactElement;
  disabledIcon?: string | React.ReactElement;
  activeIcon?: string | React.ReactElement;
  isActive?: boolean;
  iconPosition?: "left" | "right";
  children?: React.ReactNode;
  className?: string;
};

const BasicButton = ({
  sx,
  onClick,
  title,
  type = "button",
  inputAdornment = null,
  disabled = false,
  cls,
  variant = "primary",
  size = "medium",
  responsiveBreakpoints,
  width,
  icon,
  disabledIcon,
  activeIcon,
  isActive = true,
  iconPosition = "left",
  children,
  className,
}: ButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [activeState, setActiveState] = useState(0);

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const componentIdRef = React.useRef<string>(
    `basic-button-${Math.random().toString(36).substr(2, 9)}`,
  );

  const responsiveBreakpointsStr = useMemo(
    () =>
      responsiveBreakpoints ? JSON.stringify(responsiveBreakpoints) : null,
    [responsiveBreakpoints],
  );

  React.useEffect(() => {
    if (!responsiveBreakpointsStr || !buttonRef.current) return;

    const componentId = componentIdRef.current;
    const breakpoints = JSON.parse(responsiveBreakpointsStr);

    let cssRules = "";
    Object.entries(breakpoints).forEach(
      ([breakpointName, config]: [string, any]) => {
        cssRules += `@media ${config.mediaQuery} {`;

        if (config.hidden) {
          cssRules += `[data-component-id="${componentId}"] { display: none !important; }`;
        }

        if (config.size || config.customSize) {
          const sizeConfig = getSizeConfig(
            config.size || "medium",
            variant,
            config.customSize,
          );
          cssRules += `[data-component-id="${componentId}"] { ${sizeConfig} }`;
        }

        cssRules += "}";
      },
    );

    if (cssRules) {
      const styleElement = document.createElement("style");
      styleElement.setAttribute("data-component-id", componentId);
      styleElement.textContent = cssRules;
      document.head.appendChild(styleElement);

      buttonRef.current.setAttribute("data-component-id", componentId);

      return () => {
        const style = document.querySelector(
          `style[data-component-id="${componentId}"]`,
        );
        if (style) style.remove();
      };
    }
  }, [responsiveBreakpointsStr, variant]);

  const getSizeConfig = (
    size: ButtonSize,
    variant: ButtonVariant,
    customSize?: CustomSize,
  ): string => {
    if (customSize) {
      const styles: string[] = [];

      if (customSize.width !== undefined) {
        const width =
          typeof customSize.width === "number"
            ? `${customSize.width}px`
            : customSize.width;
        styles.push(`width: ${width} !important;`);
      }

      if (customSize.height !== undefined) {
        const height =
          typeof customSize.height === "number"
            ? `${customSize.height}px`
            : customSize.height;
        styles.push(`height: ${height} !important;`);
      }

      if (customSize.padding !== undefined) {
        styles.push(`padding: ${customSize.padding} !important;`);
      }

      if (customSize.borderRadius !== undefined) {
        styles.push(`border-radius: ${customSize.borderRadius} !important;`);
      }

      if (customSize.fontSize !== undefined) {
        styles.push(`font-size: ${customSize.fontSize} !important;`);
      }

      if (customSize.gap !== undefined) {
        styles.push(`gap: ${customSize.gap} !important;`);
      }

      if (customSize.lineHeight !== undefined) {
        styles.push(`line-height: ${customSize.lineHeight} !important;`);
      }

      if (customSize.letterSpacing !== undefined) {
        styles.push(`letter-spacing: ${customSize.letterSpacing} !important;`);
      }

      return styles.join(" ");
    }

    const baseStyles = {
      small:
        "height: 32px !important; padding: 6px 12px !important; border-radius: 10px !important; font-size: 14px !important; gap: 8px !important;",
      medium:
        "height: 40px !important; padding: 11px 15px !important; border-radius: 12px !important; font-size: 14px !important; gap: 10px !important;",
      large:
        "height: 48px !important; padding: 12px 24px !important; border-radius: 14px !important; font-size: 16px !important; gap: 12px !important;",
    };

    const iconStyles = {
      small:
        "width: 32px !important; height: 32px !important; padding: 8px !important;",
      medium:
        "width: 40px !important; height: 40px !important; padding: 10px !important;",
      large:
        "width: 48px !important; height: 48px !important; padding: 14px !important;",
    };

    if (
      variant === "removeIcon" ||
      variant === "iconOutline" ||
      variant === "favorite"
    ) {
      return iconStyles[size];
    }

    return baseStyles[size];
  };

  const renderIcon = (
    iconProp: string | React.ReactElement | undefined,
    alt: string,
    className?: string,
    style?: React.CSSProperties,
  ) => {
    if (!iconProp) return null;

    if (typeof iconProp === "string") {
      return (
        <img src={iconProp} alt={alt} className={className} style={style} />
      );
    }

    return React.cloneElement(iconProp, {
      style: {
        ...iconProp.props.style,
        ...style,
      },
      className: className,
    });
  };

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (variant === "circleIcon") {
      setActiveState((prev) => (prev === 0 ? 1 : 0));
    }
    if (onClick) onClick(e);
  };

  return (
    <button
      ref={buttonRef}
      disabled={disabled}
      type={type}
      className={cn(
        classes.basicButtonStyles,
        {
          [classes.primary]: variant === "primary",
          [classes.secondary]: variant === "secondary",
          [classes.danger]: variant === "danger",
          [classes.remove]: variant === "remove",
          [classes.removeIcon]: variant === "removeIcon" && !title && !children,
          [classes.iconOutline]:
            variant === "iconOutline" && !title && !children,
          [classes.circleIcon]: variant === "circleIcon" && !title && !children,
          [classes.third]: variant === "third",
          [classes.favorite]: variant === "favorite" && !title && !children,
          [classes.chart]: variant === "chart",
          [classes.disabled]: disabled,
          [classes.noTitle]:
            !title &&
            !children &&
            variant !== "removeIcon" &&
            variant !== "iconOutline" &&
            variant !== "circleIcon" &&
            variant !== "favorite" &&
            variant !== "chart",
          [classes.small]: size === "small",
          [classes.medium]: size === "medium",
          [classes.large]: size === "large",
          [classes.active]:
            (variant === "removeIcon" ||
              variant === "iconOutline" ||
              variant === "chart") &&
            (isActive || isHovered),
          [cls]: !!cls,
        },
        className,
      )}
      style={{
        ...sx,
        ...(width && {
          width: typeof width === "number" ? `${width}px` : width,
          minWidth: typeof width === "number" ? `${width}px` : width,
        }),
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {variant === "chart" ? (
        <>
          {isActive || isHovered ? (
            <img
              src={ActiveChartIcon}
              alt="chart active"
              style={{
                width: size === "large" ? 20 : size === "medium" ? 18 : 16,
                height: size === "large" ? 20 : size === "medium" ? 18 : 16,
              }}
            />
          ) : (
            <img
              src={DefaultChartIcon}
              alt="chart"
              style={{
                width: size === "large" ? 20 : size === "medium" ? 18 : 16,
                height: size === "large" ? 20 : size === "medium" ? 18 : 16,
              }}
            />
          )}
          {children && <span>{children}</span>}
        </>
      ) : variant === "favorite" ? (
        isActive || isHovered ? (
          <img
            src={ActiveFavoriteIcon}
            alt="favorite active"
            style={{
              width: size === "large" ? 20 : size === "medium" ? 18 : 16,
              height: size === "large" ? 20 : size === "medium" ? 18 : 16,
            }}
          />
        ) : (
          <img
            src={DefaultFavoriteIcon}
            alt="favorite"
            style={{
              width: size === "large" ? 20 : size === "medium" ? 18 : 16,
              height: size === "large" ? 20 : size === "medium" ? 18 : 16,
            }}
          />
        )
      ) : children ? (
        <>
          {iconPosition === "left" &&
            icon &&
            variant === "primary" &&
            renderIcon(icon, "icon", undefined, {
              width: size === "large" ? 20 : size === "medium" ? 18 : 16,
              height: size === "large" ? 20 : size === "medium" ? 18 : 16,
              marginRight: title ? "8px" : "0",
            })}
          {children}
          {iconPosition === "right" &&
            icon &&
            variant === "primary" &&
            renderIcon(icon, "icon", undefined, {
              width: size === "large" ? 20 : size === "medium" ? 18 : 16,
              height: size === "large" ? 20 : size === "medium" ? 18 : 16,
              marginLeft: title ? "8px" : "0",
            })}
        </>
      ) : (
        <>
          {iconPosition === "left" &&
            variant !== "primary" &&
            (variant === "removeIcon"
              ? disabled
                ? (() => {
                    return disabledIcon &&
                      (typeof disabledIcon === "string"
                        ? disabledIcon.trim() !== ""
                        : true)
                      ? renderIcon(disabledIcon, "disabled icon", undefined, {
                          width:
                            size === "large" ? 20 : size === "medium" ? 18 : 16,
                          height:
                            size === "large" ? 20 : size === "medium" ? 18 : 16,
                        })
                      : renderIcon(
                          DisabledTrashIcon,
                          "disabled trash",
                          undefined,
                          {
                            width:
                              size === "large"
                                ? 20
                                : size === "medium"
                                  ? 18
                                  : 16,
                            height:
                              size === "large"
                                ? 20
                                : size === "medium"
                                  ? 18
                                  : 16,
                          },
                        );
                  })()
                : isActive || isHovered
                  ? renderIcon(activeIcon || icon, "active icon", undefined, {
                      width:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                      height:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                    })
                  : icon
                    ? renderIcon(icon, "icon", undefined, {
                        width:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                        height:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                      })
                    : null
              : variant === "iconOutline"
                ? disabled
                  ? renderIcon(DisabledCopyIcon, "disabled copy", undefined, {
                      width:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                      height:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                    })
                  : icon
                    ? renderIcon(icon, "icon", undefined, {
                        width:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                        height:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                      })
                    : null
                : variant === "circleIcon"
                  ? disabled && disabledIcon
                    ? renderIcon(disabledIcon, "icon", classes.disabledIcon)
                    : activeState === 1
                      ? renderIcon(
                          activeIcon || icon,
                          "active icon",
                          undefined,
                          {
                            transform: "rotate(180deg)",
                            transition: "transform 0.2s",
                          },
                        )
                      : isHovered
                        ? renderIcon(
                            activeIcon || icon,
                            "hover icon",
                            undefined,
                            {
                              transform: "rotate(180deg)",
                              transition: "transform 0.2s",
                            },
                          )
                        : icon
                          ? renderIcon(icon, "icon")
                          : null
                  : disabled && disabledIcon
                    ? renderIcon(disabledIcon, "icon", classes.disabledIcon)
                    : icon
                      ? renderIcon(
                          icon,
                          "icon",
                          !isActive ? classes.disabledIcon : "",
                        )
                      : null)}
          {inputAdornment && (
            <img
              style={{ marginRight: title ? "0" : "0" }}
              src={inputAdornment}
              alt="button input"
            />
          )}
          {iconPosition === "left" &&
            icon &&
            variant === "primary" &&
            !children &&
            renderIcon(icon, "icon", undefined, {
              width: size === "large" ? 20 : size === "medium" ? 18 : 16,
              height: size === "large" ? 20 : size === "medium" ? 18 : 16,
              marginRight: title ? "0" : "0",
            })}
          {title && <span>{title}</span>}
          {iconPosition === "right" &&
            icon &&
            variant === "primary" &&
            !children &&
            renderIcon(icon, "icon", undefined, {
              width: size === "large" ? 20 : size === "medium" ? 18 : 16,
              height: size === "large" ? 20 : size === "medium" ? 18 : 16,
              marginLeft: title ? "8px" : "0",
            })}
          {iconPosition === "right" &&
            variant !== "primary" &&
            (variant === "removeIcon"
              ? disabled
                ? (() => {
                    return disabledIcon &&
                      (typeof disabledIcon === "string"
                        ? disabledIcon.trim() !== ""
                        : true)
                      ? renderIcon(disabledIcon, "disabled icon", undefined, {
                          width:
                            size === "large" ? 20 : size === "medium" ? 18 : 16,
                          height:
                            size === "large" ? 20 : size === "medium" ? 18 : 16,
                        })
                      : renderIcon(
                          DisabledTrashIcon,
                          "disabled trash",
                          undefined,
                          {
                            width:
                              size === "large"
                                ? 20
                                : size === "medium"
                                  ? 18
                                  : 16,
                            height:
                              size === "large"
                                ? 20
                                : size === "medium"
                                  ? 18
                                  : 16,
                          },
                        );
                  })()
                : isActive || isHovered
                  ? renderIcon(activeIcon || icon, "active icon", undefined, {
                      width:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                      height:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                    })
                  : icon
                    ? renderIcon(icon, "icon", undefined, {
                        width:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                        height:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                      })
                    : null
              : variant === "iconOutline"
                ? disabled
                  ? renderIcon(DisabledCopyIcon, "disabled copy", undefined, {
                      width:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                      height:
                        size === "large" ? 20 : size === "medium" ? 18 : 16,
                    })
                  : icon
                    ? renderIcon(icon, "icon", undefined, {
                        width:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                        height:
                          size === "large" ? 20 : size === "medium" ? 18 : 16,
                      })
                    : null
                : variant === "circleIcon"
                  ? disabled && disabledIcon
                    ? renderIcon(disabledIcon, "icon", classes.disabledIcon)
                    : activeState === 1
                      ? renderIcon(
                          activeIcon || icon,
                          "active icon",
                          undefined,
                          {
                            transform: "rotate(180deg)",
                            transition: "transform 0.2s",
                          },
                        )
                      : isHovered
                        ? renderIcon(
                            activeIcon || icon,
                            "hover icon",
                            undefined,
                            {
                              transform: "rotate(180deg)",
                              transition: "transform 0.2s",
                            },
                          )
                        : icon
                          ? renderIcon(icon, "icon")
                          : null
                  : disabled && disabledIcon
                    ? renderIcon(disabledIcon, "icon", classes.disabledIcon)
                    : icon
                      ? renderIcon(
                          icon,
                          "icon",
                          !isActive ? classes.disabledIcon : "",
                        )
                      : null)}
        </>
      )}
    </button>
  );
};

export default BasicButton;
